<template>
  <div class="w-full h-full">
    <div
      class="w-full h-full model-view"
      v-loading="loadingEngine"
      element-loading-background="#0a232c"
    >
      <video id="playerEngineId" autoplay playsinline muted class="engineVideo w-full"></video>
    </div>
    <div class="absolute top-0 left-0 w-full h-full bg-[#0a232c]" v-if="showLoadProgress"></div>
    <!-- 模型进度条 -->
    <player-progress :loadType="loadType" :percentage="loadPercentage" v-if="showLoadProgress"></player-progress>
  </div>
</template>

<script>
import { instance as cloudRender } from 'wiz-cloud-render-vulkan'

const PlayerProgress = () => {
  return import('./PlayerProgress')
}
export default {
  name: 'ModelPreview',
  components: { PlayerProgress },
  props: {
    modelDetail: {
      type: Object,
      defalut: () => {
        return {
          filePath: '',
          materialFileId: '',
          modelFileId: '', // 模型文件id，云渲染路径又是模型加载名称
        }
      },
    },
    moveVisualAngleFlag: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      player: null, // player实例
      loadingEngine: false, // 引擎加载状态
      showLoadProgress: true, // 进度条显隐
      loadType: '', // 进度条类型
      sceneRootNode: 0, // 根节点
      loadPercentage: 0, // 进度条值
      modelEngineId: '',
      preloadEngine: null,
      loadTaskId: '',
    }
  },
  created() {
    this.loadingEngine = true
  },
  computed: {
    cloudRender() {
      return cloudRender
    },
    // 模型相关设置
    modelSetting() {
      return Wiz.globalSettings.globalConfig?.modelSetting || {}
    },
    // 云渲染协议
    cloudProtocol() {
      return this.modelSetting.cloudProtocol || 'ws'
    },
    // 云渲染服务路径
    cloudServerPath() {
      return this.modelSetting.cloudServerPathVulkan || 'renderapi.wiz.top:7082'
    },
  },
  watch: {
    async moveVisualAngleFlag() {
      this.moveToModel()
    },
  },
  async mounted() {
    this.runPlayer()
    window.addEventListener('beforeunload', async (e) => {
      if (this.player) {
        this.cloudRender.Engine.destroyEngine()
      }
    })
  },
  async beforeDestroy() {
    if (this.player) {
      this.player.Native.OperatingMode.EventPickUpNode.disconnect(this.pickupWatch)
      this.player.Native.Node.EventSelectNode.disconnect(this.selectWatch)
    }
    if (this.player) {
      this.cloudRender.Engine.destroyEngine()
    }
  },
  methods: {
    // 生成uuid
    generateGUID() {
      let d = new Date().getTime()
      if (this.performance && typeof this.performance.now === 'function') {
        d += performance.now()
      }
      const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (d + Math.random() * 16) % 16 | 0
        d = Math.floor(d / 16)
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
      })
      return uuid
    },
    // 设置模型位置
    async setModelPos() {
      const longitude = this.modelSetting.cloudLongtitude || 114
      const latitude = this.modelSetting.cloudLatitude || 24
      const cloudTrans = this.modelSetting.cloudTrans || [0, 0, 0]

      const elevation = await this.player.Native.GisPlatform.queryDEM([[longitude, latitude]])[0]
      const transform = await this.player.Native.GisPlatform.makeGisTransform([longitude, latitude, elevation || 0])
      const obj = Matrix4.decompose(transform)
      const rootNode = await this.player.Native.Node.getRoot()
      let position = []
      if (cloudTrans.length === 3) {
        position = [obj.position[0] + cloudTrans[0], obj.position[1] + cloudTrans[1], obj.position[2] + cloudTrans[2]]
      }
      console.log('obj.position', obj.position)
      console.log('cloudTrans', cloudTrans)
      console.log('position', position)
      await this.player.Native.Node.setProperty(rootNode, {
        localTrans: position,
        localRot: obj.quaternion
      })
      this.moveToModel()
    },
    // 移动视角到模型
    async moveToModel() {
      const perspective = this.modelSetting.cloudModelVisual || {}
      if (perspective.eye) {
        this.player.Native.Camera.moveTo(perspective.eye, perspective.target, perspective.up, 2)
      } else {
        await this.player.Native.Camera.moveToNode(rootNode, 2)
      }
    },
    // 加载player引擎 sdk
    async runPlayer(engineId) {
      this.preloadEngine = engineId || null
      this.modelEngineId = this.generateGUID()
      const param = {
        signalServer: `${this.cloudProtocol}://${this.cloudServerPath}`,
        options: {
          videoDom: document.getElementById('playerEngineId'),
          userTimeout: 1000 * 60 * 60, // 用户操作超时设置，毫秒
          engineId: engineId || this.modelEngineId, // 引擎会话id，如果已存在则连接，没有则创建，会话在断开连接后超时自动关闭
          exitTimeout: 1000 * 60, // 用户超时后多久断开会话
          maxTimeout: 1000, // 会话超时时间，允许掉线后引擎保存多久，在该时间内可重新连接到会话，否则会创建新的会话
          // openDebug: true,
        },
      }
      const { data } = await this.cloudRender.Engine.loadEngine(param)
      this.player = data.getDataInfo()
      this.cloudRender.Dispatch.on('Engine', async (e) => {
        if (e.type === 'UserTimeOut') {
          // 用户操作超时
          console.error('用户操作超时')
        }
        if (e.type === 'TimeOutExit') {
          // 断开会话
          console.error('超时断开')
        }
      })
      window.player = this.player
      window.cloudRender = this.cloudRender
      this.loadingEngine = false
      // 实例化完成将实例化后的player传出去
      this.$emit('sendPlayer', this.player, this.cloudRender)
      this.initPlayer(engineId)
    },
    // 初始化player
    async initPlayer(engineId) {
      const settingsFileUrl = `data://../apps/${this.modelSetting.folderName}/settings.json`
      const settingsData = await this.player.Native.File.loadText(settingsFileUrl)
      if (settingsData) {
        const defaultSettings = JSON.parse(settingsData)
        try {
          const res = await this.cloudRender.Setting.set(defaultSettings.Show)
        } catch (error) {
          console.error(error)
        }
      }

      // this.player.Native.Tools.EventBrowseLClick.connect((data) => {
      //   console.log('左键', data)
      // })
      // 加载全局地图
      await this.cloudRender.GIS.loadGis(this.modelSetting.globalMapPath)
      await this.loadModelSdk()
      this.cloudRender.Setting.set({
        name: 'func.nodetips.enable',
        value: false,
      })
      cloudRender.Setting.set({
        name: 'display.autoUpdateFrame',
        type: 'bool',
        value: false,
      })
      // 点选监听
      this.player.Native.OperatingMode.EventPickUpNode.connect(this.pickupWatch)
      this.player.Native.Node.EventSelectNode.connect(this.selectWatch)
    },
    // 加载模型
    async loadModelSdk() {
      this.cloudRender.Dispatch.on('loadNode', this.loadModelConnect)
      const query = {
        targetUrl: this.modelSetting.cloudModelPath,
      }
      const res = await this.cloudRender.Node.addNode(query)
    },
    // 加载、下载进度
    loadModelConnect: _.throttle(async function loadMsg(res) {
      if (res.status && res.data && res.data.type === 'nodeDecals') {
        switch (res.data.currentState) {
          case 1:
            this.showLoadProgress = true
            this.loadType = '贴图加载中...'
            this.loadPercentage = _.round(res.data.process, 2)
            break
          case 3:
            this.showLoadProgress = true
            this.loadType = '贴图下载中...'
            this.loadPercentage = _.round(res.data.process, 2)
            break
          case 4:
            this.showLoadProgress = false
            this.loadType = ''
            this.loadPercentage = 0
            break
          case 5:
            this.showLoadProgress = false
            this.loadType = ''
            this.loadPercentage = 0
            break
          default:
            break
        }
      } else if (res.status && res.data && res.data.type === 'nodeStatus') {
        switch (res.data.currentState) {
          case 1:
            this.showLoadProgress = true
            this.loadType = '模型加载中...'
            this.loadPercentage = _.round(res.data.process, 2)
            break
          case 3:
            this.showLoadProgress = true
            this.loadType = '模型下载中...'
            this.loadPercentage = _.round(res.data.process, 2)
            break
          case 4:
            this.showLoadProgress = false
            this.loadType = ''
            this.loadPercentage = 0
            this.cloudRender.Dispatch.off('loadNode', this.loadModelConnect)
            this.$emit('loadModelEnd')

            this.setModelPos()
            break
          case 5:
            this.showLoadProgress = false
            this.loadType = ''
            this.loadPercentage = 0
            break
          default:
            break
        }
      } else if (!res.status) {
        this.$message.warning(res.message)
        this.showLoadProgress = false
        this.loadType = ''
        this.loadPercentage = 0
      }
    }, 500),
    // 点选选中监听
    async pickupWatch(data) {
      let res = {}
      if (data.node) {
        res = await this.player.Native.Node.getProperty(data.node, ['code', 'name'])
      }
      this.upDateCodeMsg(res)
    },
    // // 接口选中监听
    async selectWatch(node, status) {
      let res = {}
      if (status && node) {
        res = await this.player.Native.Node.getProperty(node, ['code', 'name'])
      }
      this.upDateCodeMsg(res)
    },
    // 选中更新
    async upDateCodeMsg(res) {
      this.$emit('updateModelNode', res || {})
    },
  },
}
</script>

<style lang="scss" scoped>
.engineVideo {
  height: 100%;
  object-fit: fill;
}

.model-view {
  transform: scale(1, 1);
  transform-origin: 'top left';
}
</style>
